/**
 * Making a design from a Word document or a PDF.
 *
 * Built like bringing in a spreadsheet, and for the same reasons. The file is read
 * in this tab — `@broadpaper/import` parses the Word document or reconstructs the
 * PDF, and the builder turns what it found into an ordinary design — so a document
 * full of a client's details never leaves the machine it is on, and the service
 * never runs a parser over something a stranger chose.
 *
 * Three things this screen is careful about.
 *
 * **Nothing is saved until the person has seen the result.** A preview of the
 * design, what was found in the document, and every part that could not be carried
 * across, before one press of "Make it a design". An import that silently dropped a
 * chart would be discovered in front of a client.
 *
 * **It says what an import is.** A starting point, not a copy — said on the screen,
 * because the alternative is somebody expecting a pixel-perfect clone of a document
 * built from text boxes and deciding the product is broken.
 *
 * **Reading is free; making a design is Pro.** Reading costs BroadPaper nothing, so
 * a Free workspace can see exactly what an import would give it, and the upgrade is
 * offered at the moment it is worth something rather than as a wall before anything
 * has happened.
 */
import { useEffect, useRef, useState } from "react";
import { createRegistry } from "@broadpaper/blocks";
import { createReportViewer, type ReportViewer } from "@broadpaper/renderer";
// Only the error class and the limits, statically; the readers are loaded on first use.
import { ImportError, IMPORT_LIMITS, type BuiltImport, type CanvasFactory, type ImportNotice } from "@broadpaper/import";
import { ApiError, api, helpUrl } from "../api.js";
import { go } from "../router.js";
import { Button, Card, Field, Icon, Note, bytes, count } from "../ui/index.js";
import { atLimit, upgradeTarget, useWorkspace } from "../workspace.js";

const registry = createRegistry();

const ACCEPT = ".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Help for this screen, each written out in full so `tests/site/support.test.ts`
 * can find it and fail if the section it points at goes.
 */
const HELP = {
  importing: helpUrl("/support/designs/#importing"),
  word: helpUrl("/support/designs/#import-word"),
  pdf: helpUrl("/support/designs/#import-pdf"),
  limits: helpUrl("/support/designs/#import-limits")
} as const;

type Readers = typeof import("@broadpaper/import");
type PdfJs = Parameters<Readers["readPdf"]>[0];

/**
 * The Word and PDF readers and the builder, loaded when a file is chosen.
 *
 * The same argument as pdf.js below, at a smaller size: a couple of hundred
 * kilobytes of parser that only the few people importing a document ever run, and
 * every other screen would otherwise download with the first chunk.
 */
let readers: Promise<Readers> | null = null;

function loadReaders(): Promise<Readers> {
  readers ??= import("@broadpaper/import");
  return readers;
}

/**
 * pdf.js, loaded when a PDF is chosen and not before.
 *
 * It is a large library with a worker of its own, and a static import would put it
 * in the first chunk every visitor downloads — so opening the home page would pay for
 * reading PDFs. Once loaded it is kept, for the same reason the designer's engine is.
 */
let pdfjs: Promise<PdfJs> | null = null;

function loadPdfJs(): Promise<PdfJs> {
  pdfjs ??= (async () => {
    const [module, worker] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
    module.GlobalWorkerOptions.workerSrc = worker.default;
    return module as unknown as PdfJs;
  })();
  return pdfjs;
}

/** A page drawn onto an ordinary canvas, which is how a PDF's images are cropped out of it. */
const domCanvas: CanvasFactory = (width, height) => {
  const element = document.createElement("canvas");
  element.width = Math.max(1, Math.ceil(width));
  element.height = Math.max(1, Math.ceil(height));
  return { context: element.getContext("2d"), toPngDataUrl: () => element.toDataURL("image/png") };
};

/**
 * A document's typeface, as one BroadPaper embeds.
 *
 * Cloud may only name fonts `packages/cloud-fonts` embeds — CLAUDE.md's measurement
 * trap, closed — so a document set in Garamond comes across in the nearest of those:
 * a serif as Source Serif 4, anything else as Inter. Guessing "serif" from a family
 * name is crude and is right for the fonts business documents are actually set in.
 */
function mapFont(family: string | undefined): string | undefined {
  if (!family) return undefined;
  const serif = /serif|times|georgia|garamond|cambria|baskerville|palatino|book antiqua|minion|caslon|didot|bodoni|merriweather|playfair|charter|constantia/i.test(family) && !/sans/i.test(family);
  return serif ? "Source Serif 4" : "Inter";
}

/** Something that stopped a file being read or saved, kept on screen with somewhere to go. */
interface Problem {
  message: string;
  hint?: string | undefined;
  help: string;
}

interface Result {
  built: BuiltImport;
  format: "docx" | "pdf";
  filename: string;
  notices: ImportNotice[];
}

type Stage = { kind: "idle" } | { kind: "reading"; filename: string } | { kind: "read"; result: Result };

/** The reader's notices and the builder's, once each. The builder may repeat what the reader said. */
function uniqueNotices(notices: ImportNotice[]): ImportNotice[] {
  const seen = new Set<string>();
  return notices.filter((n) => {
    const key = `${n.code}:${n.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function withoutExtension(filename: string): string {
  return filename.replace(/\.(docx|pdf)$/i, "").trim() || "Imported document";
}

export function ImportDocument() {
  const { view, refresh } = useWorkspace();
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [problem, setProblem] = useState<Problem | null>(null);
  const [over, setOver] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const allowance = view.limits.importsPerMonth;
  const usedUp = allowance > 0 && view.usage.importsThisMonth >= allowance;
  const designsFull = atLimit(view, "templates");
  const upgrade = upgradeTarget(view);

  const read = async (file: File) => {
    setProblem(null);
    if (file.size > IMPORT_LIMITS.maxBytes) {
      setProblem({
        message: `That file is ${bytes(file.size)}, and BroadPaper reads documents up to ${bytes(IMPORT_LIMITS.maxBytes)}.`,
        hint: "Nothing has been read. A copy without large pictures, or just the pages you need, usually gets under it.",
        help: HELP.importing
      });
      return;
    }
    setStage({ kind: "reading", filename: file.name });
    try {
      // A frame, so "Reading…" paints before a synchronous parse blocks the thread.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      const [data, { buildTemplate, readDocx, readPdf, sniffDocument }] = await Promise.all([file.arrayBuffer().then((b) => new Uint8Array(b)), loadReaders()]);
      const format = sniffDocument(data, file.name);
      const reading = format === "docx" ? readDocx(data) : await readPdf(await loadPdfJs(), data, { canvas: domCanvas });
      const title = withoutExtension(file.name);
      const built = buildTemplate(reading, { registry, name: title, mapFont: (family) => mapFont(family) });
      setName(title);
      setStage({ kind: "read", result: { built, format, filename: file.name, notices: uniqueNotices([...reading.notices, ...built.notices]) } });
    } catch (e) {
      setStage({ kind: "idle" });
      if (e instanceof ImportError) {
        setProblem({ message: e.message, hint: e.hint, help: /pdf/i.test(file.name) ? HELP.pdf : HELP.word });
      } else {
        // A reader that throws something other than its own error is a bug, not a
        // bad file, and the person should be told as much rather than blamed.
        setProblem({
          message: "BroadPaper couldn't read this document.",
          hint: "Nothing has been saved. If it keeps happening with this file, email support@broadpaper.com and say what program made it.",
          help: HELP.importing
        });
        console.error("[broadpaper] import failed", e);
      }
    }
  };

  const make = async (result: Result) => {
    setSaving(true);
    setProblem(null);
    try {
      const title = name.trim() || withoutExtension(result.filename);
      const { built } = result;
      const created = await api.createImport({
        name: title,
        format: result.format,
        filename: result.filename,
        template: built.template,
        ...(built.dataset ? { dataset: { name: `${title} — table`, columns: built.dataset.columns, rows: built.dataset.rows } } : {}),
        stats: { ...built.stats }
      });
      await refresh();
      go(`/designs/${created.template.id}`);
    } catch (e) {
      setSaving(false);
      if (e instanceof ApiError) setProblem({ message: e.message, hint: e.hint, help: e.code === "limit-reached" ? HELP.limits : HELP.importing });
      else setProblem({ message: "We couldn't make that design.", hint: "Nothing has been saved. Try again in a moment.", help: HELP.importing });
    }
  };

  /* --------------------------------- the result -------------------------------- */

  if (stage.kind === "read") {
    const { result } = stage;
    const { stats, dataset } = result.built;
    const found = [
      stats.pages ? `${count(stats.pages)} page${stats.pages === 1 ? "" : "s"}` : null,
      stats.headings ? `${count(stats.headings)} heading${stats.headings === 1 ? "" : "s"}` : null,
      stats.paragraphs ? `${count(stats.paragraphs)} paragraph${stats.paragraphs === 1 ? "" : "s"}` : null,
      stats.lists ? `${count(stats.lists)} list${stats.lists === 1 ? "" : "s"}` : null,
      stats.tables ? `${count(stats.tables)} table${stats.tables === 1 ? "" : "s"}` : null,
      stats.images ? `${count(stats.images)} picture${stats.images === 1 ? "" : "s"}` : null
    ].filter(Boolean);

    return (
      <div className="wrap--wide stack" style={{ gap: 20 }}>
        <div className="head" style={{ marginBottom: 0 }}>
          <div className="head__title">
            <span className="eyebrow">
              <a href="/designs" style={{ textDecoration: "none" }}>
                Designs
              </a>{" "}
              · Import
            </span>
            <h1>Here is the design from {result.filename}</h1>
            <p className="head__lede">
              An import is a starting point, not a copy: the structure and the words of your document as a design you can change like any other. Nothing
              has been saved yet.
            </p>
          </div>
          <div className="head__actions">
            <Button variant="quiet" onClick={() => setStage({ kind: "idle" })} disabled={saving}>
              Choose another file
            </Button>
          </div>
        </div>

        {problem ? (
          <Note tone="bad" title={problem.message}>
            {problem.hint ? `${problem.hint} ` : ""}
            <a href={problem.help} target="_blank" rel="noreferrer">
              What to do about this
            </a>
          </Note>
        ) : null}

        <div className="import-result">
          <Preview key={result.filename} built={result.built} />

          <div className="stack" style={{ gap: 14 }}>
            <Card>
              <div className="card__body stack" style={{ gap: 10 }}>
                <strong>What BroadPaper found</strong>
                <p className="small muted">{found.length ? `${found.join(", ")}.` : "Very little it could use."}</p>
                {dataset ? (
                  <p className="small">
                    <Icon name="data" className="inline-icon" /> The table{" "}
                    <strong>{dataset.columns.filter((c) => c.include).map((c) => c.label).slice(0, 4).join(", ")}</strong> became data you can refill —{" "}
                    {count(dataset.rows.length)} row{dataset.rows.length === 1 ? "" : "s"}. Point the design at next month's figures and the table fills in from
                    them.
                  </p>
                ) : null}
                {stats.imagesDropped ? (
                  <p className="small" style={{ color: "var(--amber)" }}>
                    {count(stats.imagesDropped)} picture{stats.imagesDropped === 1 ? "" : "s"} couldn't be carried across.
                  </p>
                ) : null}
              </div>
            </Card>

            {result.notices.length ? (
              <Note tone={result.notices.some((n) => n.level === "warn") ? "ask" : "info"} title="What didn't come across as it was">
                <ul className="notice-list">
                  {result.notices.map((notice, i) => (
                    <li key={i}>{notice.message}</li>
                  ))}
                </ul>
                <a href={result.format === "pdf" ? HELP.pdf : HELP.word} target="_blank" rel="noreferrer">
                  What imports well, and what doesn't
                </a>
              </Note>
            ) : null}

            <Card>
              <div className="card__body stack" style={{ gap: 12 }}>
                <Field label="What should this design be called?" id="import-name">
                  <input id="import-name" className="input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} disabled={saving} />
                </Field>

                {allowance <= 0 ? (
                  <Note tone="info" title="Importing is part of Pro">
                    {upgrade && upgrade.limits.importsPerMonth > 0
                      ? `${upgrade.name} makes up to ${count(upgrade.limits.importsPerMonth)} documents a month into designs, and removes the BroadPaper line from your documents. `
                      : ""}
                    <a href="/account">See {upgrade?.name ?? "the plans"}</a>
                  </Note>
                ) : usedUp ? (
                  <Note tone="ask" title={`You have imported all ${count(allowance)} documents your plan includes this month`}>
                    The count resets on the first of the month.{" "}
                    <a href={HELP.limits} target="_blank" rel="noreferrer">
                      How imports are counted
                    </a>
                  </Note>
                ) : designsFull ? (
                  <Note tone="ask" title={`You are using all ${count(view.limits.templates)} designs your plan includes`}>
                    Delete a design you no longer need to make room for this one.
                  </Note>
                ) : (
                  <>
                    <Button variant="primary" icon="design" busy={saving} disabled={!name.trim()} onClick={() => void make(result)}>
                      Make it a design
                    </Button>
                    <span className="small muted">
                      Uses 1 of this month's {count(allowance)} imports — {count(Math.max(0, allowance - view.usage.importsThisMonth))} left.
                    </span>
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------- the drop zone ------------------------------ */

  const busy = stage.kind === "reading";

  return (
    <div className="wrap--narrow stack" style={{ gap: 22 }}>
      <div className="head__title">
        <span className="eyebrow">
          <a href="/designs" style={{ textDecoration: "none" }}>
            Designs
          </a>{" "}
          · Import
        </span>
        <h1>Import a Word document or a PDF</h1>
        <p className="head__lede">
          Start a design from a document you already have — last year's invoice, a quote, a report. BroadPaper reads it on your own machine, shows you the
          design it made, and saves nothing until you say so.
        </p>
      </div>

      {allowance <= 0 ? (
        <Note tone="info" title="Making a design from a document is part of Pro">
          You can still read a document here and see what it would give you — that part is free.{" "}
          <a href={HELP.limits} target="_blank" rel="noreferrer">
            How importing works on each plan
          </a>
        </Note>
      ) : null}

      {problem ? (
        <Note tone="bad" title={problem.message}>
          {problem.hint ? `${problem.hint} ` : ""}
          <a href={problem.help} target="_blank" rel="noreferrer">
            What to do about this
          </a>
        </Note>
      ) : null}

      <label
        className={`drop${over ? " drop--over" : ""}${busy ? " drop--busy" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files[0];
          if (file && !busy) void read(file);
        }}
      >
        {busy ? (
          <>
            <span className="spinner spinner--lg" />
            <strong>Reading {stage.filename}…</strong>
            <span className="small muted">This happens on your own machine. A long PDF can take a few seconds.</span>
          </>
        ) : (
          <>
            <Icon name="upload" className="drop__icon" />
            <strong style={{ fontSize: 17 }}>{over ? "Drop it here" : "Drop a document here, or choose one"}</strong>
            <span className="small muted">Word (.docx) or PDF · up to {bytes(IMPORT_LIMITS.maxBytes)}</span>
            <input
              type="file"
              accept={ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void read(file);
                e.target.value = "";
              }}
            />
          </>
        )}
      </label>

      <Note tone="info" title="What comes across">
        Headings, paragraphs, lists, tables, pictures, the page size and margins, and the header and footer. A table of rows — an invoice's lines — becomes
        data the design can be refilled from. Word documents carry far more structure than PDFs, so if you have both, import the Word file.{" "}
        <a href={HELP.importing} target="_blank" rel="noreferrer">
          More about importing
        </a>
      </Note>
    </div>
  );
}

/**
 * The design as it would be made, drawn with the document's own table as its data.
 *
 * The SDK's read-only viewer with its toolbar hidden: the toolbar prints, and a
 * document printed from here would be a PDF no plan limit or mark ever saw.
 */
function Preview({ built }: { built: BuiltImport }) {
  const mount = useRef<HTMLDivElement>(null);
  const viewer = useRef<ReportViewer | null>(null);

  useEffect(() => {
    const element = mount.current;
    if (!element) return;
    viewer.current = createReportViewer(element, {
      template: built.template,
      registry,
      dataSources: built.dataSources,
      data: built.data,
      locale: "en-GB",
      currency: "GBP",
      toolbar: false,
      interactions: false,
      fit: "width",
      label: "The imported design"
    });
    return () => {
      viewer.current?.destroy();
      viewer.current = null;
    };
  }, [built]);

  return <div ref={mount} className="history__preview import-result__preview" />;
}
